import Base from "@/controllers/base";
import ProgressBar from "progressbar.js";
import { MaskInput } from "maska";
import Message from "element-ui/lib/message";
import MessageBox from "element-ui/lib/message-box";
import rootWindow from "@/default/utils/root_window";

const flashTypes = {
  notice: "success",
  success: "success",
  info: "info",
  error: "error",
  alert: "warning",
};
export default class NApp extends Base {
  static zIndex() {
    let elems = [...document.querySelectorAll('[style*="z-index"]')];
    elems = elems.filter((e) => e.id != "bullet-footer");
    let zIndexes = elems.map((e) => parseInt(getComputedStyle(e).zIndex) || 0);
    // the 500000 limit is because the dynamsoft-dwt-dialogProgress has z-index of 500002
    zIndexes = [2000, ...zIndexes].filter((x) => x < 500000);
    zIndexes = zIndexes.sort((a, b) => a - b);
    return (zIndexes.pop() || 2000) + 1;
  }

  static reload() {
    window.location.reload();
  }

  static loading() {
    if (NApp.progressPresent()) {
      if (NApp.progressBlank()) NApp.bar = null;
      NApp.bar ||= new ProgressBar.Line("#progress", {
        strokeWidth: 2,
        easing: "easeInOut",
        duration: 800,
        color: "#5e93ff",
        trailColor: "transparent",
        trailWidth: 1,
        svgStyle: { width: "100%", height: "100%", display: "block" },
      });
      NApp.bar.set(0);
      NApp.bar.animate(1);
    }
  }

  static loaded() {
    if (NApp.progressPresent() && NApp.bar) {
      NApp.bar.set(0);
    }
  }

  static progressPresent() {
    return document.querySelector("#progress") != null;
  }
  static progressBlank() {
    return document.querySelector("#progress").childNodes.length == 0;
  }

  static decodeHtmlEntity(text) {
    const div = document.createElement("div");
    div.innerHTML = text;
    return div.textContent;
  }

  static observeVisibleElements(selectors, callback) {
    // Function to check if an element matches any of the selectors
    function matchesAnySelector(element, selectors) {
      return selectors.some((selector) => element.matches(selector));
    }

    // Create an IntersectionObserver to check visibility
    const visibilityObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Element is visible in the viewport
            callback(entry.target);
            // Stop observing the element after it becomes visible (optional)
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1, // Adjust this threshold as needed (0.1 = 10% visible)
      },
    );

    // Callback function for MutationObserver to handle new elements
    function mutationCallback(mutationsList) {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList") {
          // Check added nodes
          mutation.addedNodes.forEach((node) => {
            if (
              node.nodeType === Node.ELEMENT_NODE &&
              matchesAnySelector(node, selectors)
            ) {
              if (node.offsetParent !== null) {
                // Element is already visible
                callback(node);
              } else {
                // Observe the new element for visibility
                visibilityObserver.observe(node);
              }
            }
          });
        }
      }
    }

    // Create a MutationObserver to watch for new elements
    const mutationObserver = new MutationObserver(mutationCallback);

    // Start observing the document with the configured parameters
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    // Check existing elements in the document
    document.querySelectorAll(selectors.join(",")).forEach((element) => {
      if (element.offsetParent !== null) {
        // Element is already visible
        callback(element);
      } else {
        // Observe existing elements for visibility
        visibilityObserver.observe(element);
      }
    });

    // Fallback: in case some elements appear before observer is ready
    setTimeout(() => {
      document.querySelectorAll(selectors.join(",")).forEach((element) => {
        if (element.offsetParent !== null) {
          callback(element);
        }
      });
    }, 0);
  }

  static ensureZIndex() {
    if (!document.body.dataset.zIndexEnsured) {
      // console.log("- Ensure zIndex -");
      NApp.observeVisibleElements(
        [
          ".el-popper",
          ".el-popover",
          ".el-picker-panel",
          ".el-message-box__wrapper",
          ".v--modal-overlay",
        ],
        (elem) => {
          elem.style.zIndex = NApp.zIndex();
          let excludes = ["aria-expanded", "root"];
          let attrs = Array.from(elem.attributes);
          attrs = attrs.filter((a) => !excludes.includes(a.name));
          let display = attrs.map((a) => `${a.name}: ${a.value}`).join("; ");
          // console.log(elem.tagName + ": " + display);
        },
      );
      document.body.dataset.zIndexEnsured = true;
    }
  }

  static flashNoticeOut(message, type, duration = 2000) {
    let elem = document.querySelector("#flash-message");
    if (elem) {
      message ||= elem.dataset.message || "";
      if (!message.isBlank()) {
        Message({
          message,
          type: flashTypes[elem.dataset.type || type || "notice"],
          duration,
          showClose: true,
        }).$el.style.zIndex = 500000;
        delete elem.dataset.message;
        delete elem.dataset.type;
      }
    }
  }

  static disableLink() {
    document.querySelectorAll("a[disabled]").forEach((elem) => {
      if (elem.hasAttribute("disabled-handler")) return;
      elem.setAttribute("disabled-handler", true);
      elem.setAttribute("href", "javascript:void(0)");
      elem.addEventListener("click", (event) => {
        event.preventDefault();
      });
    });
  }

  static highlight(selector, color, duration = 2500) {
    document
      .querySelectorAll(selector)
      .forEach((elem) => elem.highlight(color, duration));
  }

  static updateElement(selector, property, value, highlight = {}) {
    document.querySelectorAll(selector).forEach((elem) => {
      elem[property] = value;
      let { color, duration } = highlight;
      if (color) {
        elem.highlight(color, duration || 2500);
      }
    });
  }

  static updateDateset(selector, property, value) {
    document.querySelectorAll(selector).forEach((elem) => {
      if (value) {
        elem.dataset[property] = value;
      } else {
        delete elem.dataset[property];
      }
    });
  }

  static addClass(selector, names) {
    document
      .querySelectorAll(selector)
      .forEach((elem) => elem.classList.add(...names.split(/\s+/)));
  }

  static removeClass(selector, names) {
    document.querySelectorAll(selector).forEach((elem) => {
      names ||= elem.classList.value;
      elem.classList.remove(...names.split(/\s+/));
    });
  }

  static autoArea() {
    document.querySelectorAll("textarea.auto-area").forEach((elem) => {
      if (elem.hasAttribute("auto-area-handler")) return;
      elem.setAttribute("auto-area-handler", true);
      elem.addEventListener("focus", () => elem.setAttribute("rows", 3));
      elem.addEventListener("blur", () => elem.setAttribute("rows", 1));
    });
  }

  static autoRemove() {
    document.querySelectorAll(".nospace, .nospace input").forEach((elem) => {
      if (elem.hasAttribute("nospace-handler")) return;
      elem.setAttribute("nospace-handler", true);
      elem.observeField(200, () => {
        if (elem.value) {
          elem.value = elem.value.replace(/\s+/g, "");
        }
      });
    });

    document.querySelectorAll(".nocomma, .nocomma input").forEach((elem) => {
      if (elem.hasAttribute("nocomma-handler")) return;
      elem.setAttribute("nocomma-handler", true);
      elem.observeField(200, () => {
        if (elem.value) {
          elem.value = elem.value.replace(/\,+/g, "");
        }
      });
    });
  }

  static autoNumber() {
    document.querySelectorAll("input.number").forEach((elem) => {
      if (elem.hasAttribute("number-handler")) return;
      elem.setAttribute("number-handler", true);
      elem.setAttribute("type", "number");
      if (!elem.getAttribute("step")) {
        elem.setAttribute("step", "0.01");
      }
    });
  }

  static autoSave() {
    // Delegate once on document so bindings survive Turbo frame swaps / refresh
    // races (dashboard loads carrier-profile via turbo-frame src). Per-element
    // auto-save-handler attributes break when DOM is restored with the attribute
    // but without listeners.
    if (document.documentElement.dataset.autoSaveDelegated) return;
    document.documentElement.dataset.autoSaveDelegated = "true";
    document.addEventListener("change", async (event) => {
      const elem = event.target?.closest?.(".auto-save");
      if (!elem) return;

      let url = elem.dataset.url;
      let format = elem.dataset.format;
      let formData = new FormData();

      if (elem.hasAttribute("target")) {
        const parts = elem.getAttribute("target").split(":");
        formData.append("column", parts.pop());
        url ||= `/api/v1/attrs/${parts.pop()}.json`;
        formData.append("type", parts.join(":"));
      }

      if (elem.hasAttribute("id")) {
        formData.append("respond_id", elem.getAttribute("id"));
      }

      if (elem.type == "checkbox") {
        formData.append("value", elem.checked ? "1" : "0");
      } else {
        formData.append("value", elem.value);
      }

      if (/\.json/.test(url) || format == "json") {
        let response = await fetch(url, {
          method: "PUT",
          headers: {
            "X-CSRF-Token": Base.authenticityToken,
          },
          body: formData,
        });
        let json = await response.json();
        if (response.ok) {
          NApp.flashNoticeOut(json.message);
          if (elem.hasAttribute("callback"))
            eval(elem.getAttribute("callback"));
        } else {
          NApp.flashNoticeOut(json.errors || json.message, "error");
        }
      } else {
        let response = await fetch(url, {
          method: "PUT",
          headers: {
            Accept: "text/vnd.turbo-stream.html",
            "X-CSRF-Token": Base.authenticityToken,
          },
          body: formData,
        });
        Turbo.renderStreamMessage(await response.text());
        NApp.dispatchTurboLoad();
      }
    });
  }

  static autoSum() {
    document.querySelectorAll(".auto-sum").forEach((elem) => {
      if (elem.hasAttribute("auto-sum-handler")) return;
      elem.setAttribute("auto-sum-handler", true);
      const fields = document.querySelectorAll(elem.dataset.field);
      var sumFunc = () => {
        let sum = 0;
        fields.forEach((field) => {
          sum += parseFloat(field.value) || 0.0;
        });
        elem.innerHTML = sum;
      };
      fields.forEach((field) => {
        field.addEventListener("change", () => {
          sumFunc();
        });
      });
      sumFunc();
    });
  }

  static cancelLink() {
    document.querySelectorAll(".cancel").forEach((elem) => {
      if (elem.hasAttribute("cancel-handler")) return;
      elem.setAttribute("cancel-handler", true);
      elem.addEventListener("click", () => {
        let section = elem.closest(".section");
        if (section) section.hide();
      });
    });
  }

  static clearForm() {
    document.querySelectorAll(".clear-btn").forEach((elem) => {
      if (elem.hasAttribute("clear-btn-handler")) return;
      elem.setAttribute("clear-btn-handler", true);
      elem.addEventListener("click", () => {
        elem
          .closest("form")
          .querySelectorAll(".clearable")
          .forEach((target) => {
            target.value = "";
            target.dispatchEvent(new CustomEvent("clear"));
          });
      });
    });
  }

  static checkAll() {
    document.querySelectorAll(".check-all").forEach((elem) => {
      if (elem.hasAttribute("check-all-handler")) return;
      elem.setAttribute("cleck-all-handler", true);
      elem.addEventListener("click", () => {
        const targets = document.querySelectorAll(`.${elem.dataset.target}`);
        if (elem.checked) {
          targets.forEach((target) => (target.checked = true));
        } else {
          targets.forEach((target) => (target.checked = false));
        }
      });
    });
  }

  static inputMask() {
    document.querySelectorAll(".us-phone, .us-phone input").forEach((elem) => {
      if (elem.tagName == "INPUT") {
        elem.dataset.maska = "(###) ###-####";
        elem.placeholder = "(___) ___-____";
      }
    });
    document.querySelectorAll(".us-ssn, .us-ssn input").forEach((elem) => {
      if (elem.tagName == "INPUT") {
        elem.dataset.maska = "###-##-####";
        elem.placeholder = "___-__-____";
      }
    });
    new MaskInput("[data-maska]");
  }

  static moneyInput() {
    new MaskInput(".money, .money input", {
      preProcess: (val) => (val ? val.replace(/[$,]/g, "") : ""),
      postProcess: (val) => {
        if (!val) return "";
        const sub = 3 - (val.includes(".") ? val.length - val.indexOf(".") : 0);
        return Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        })
          .format(val)
          .slice(1, sub ? -sub : undefined);
      },
    });
  }

  static passwordInput() {
    document.querySelectorAll('input[type="password"]').forEach((elem) => {
      if (elem.hasAttribute("password-handler")) return;
      elem.setAttribute("password-handler", true);
      if (elem.classList.contains("el-input__inner")) return;
      let eye = elem.parentElement.querySelector("i.eye");
      if (!eye) {
        elem.insertAdjacentHTML(
          "AfterEnd",
          '<i class="eye far fa-eye-slash"/>',
        );
        eye = elem.nextElementSibling;
        eye.addEventListener("click", () => {
          if (elem.type == "password") {
            elem.type = "text";
            eye.classList.remove("fa-eye-slash");
            eye.classList.add("fa-eye");
          } else {
            elem.type = "password";
            eye.classList.remove("fa-eye");
            eye.classList.add("fa-eye-slash");
          }
        });
      }
    });
  }

  static showHide() {
    document.querySelectorAll(".show-hide").forEach((target) => {
      if (target.hasAttribute("show-hide-handler")) return;
      target.setAttribute("show-hide-handler", true);
      target.addEventListener("click", async (event) => {
        let elem = event.target.closest(".show-hide");
        let rel = elem.getAttribute("rel");
        let url = elem.dataset.url;
        let shownFunc = elem.dataset.shown;
        let icon = elem.querySelector(".icon");
        let section = elem.nextElementSibling;
        if (elem.classList.contains("cancel")) section = elem.closest(".plate");
        if (rel) section = document.getElementById(rel);
        if (!section) return;
        if (section.isVisible()) {
          if (url) section.innerHTML = null;
          if (icon) {
            icon.classList.remove("icons-min");
            icon.classList.add("icons-max");
          }
          section.hide();
        } else {
          if (url) {
            let response = await fetch(url);
            section.innerHTML = await response.text();
            NApp.dispatchTurboLoad();
          }
          if (icon) {
            icon.classList.remove("icons-max");
            icon.classList.add("icons-min");
          }
          if (shownFunc) eval(shownFunc);
          section.show();
        }
      });
    });
  }

  static syncValue() {
    document.querySelectorAll("*[data-sync]").forEach((elem) => {
      if (elem.hasAttribute("data-sync-handler")) return;
      elem.setAttribute("data-sync-handler", true);
      elem.addEventListener("change", () => {
        document.querySelectorAll(elem.dataset.sync).forEach((target) => {
          target.value = elem.value;
        });
      });
    });
  }

  static turboWrapper() {
    document.querySelectorAll("a[data-turbo]").forEach((elem) => {
      if (elem.dataset.turbo == "false") return;
      if (elem.hasAttribute("data-turbo-handler")) return;
      elem.dataset.turboUrl = elem.getAttribute("href");
      elem.setAttribute("data-turbo-handler", true);
      elem.setAttribute("href", "javascript:void(0)");
      elem.setAttribute("onclick", "NApp.turboClick(this)");
    });
  }

  static async turboClick(elem) {
    try {
      let message = elem.dataset.turboConfirm;
      if (message) {
        await MessageBox.confirm(message, "Confirmation");
        await NApp.turboRequest(elem);
      } else {
        await NApp.turboRequest(elem);
      }
    } catch (ex) {
      console.log(ex);
    }
  }

  static turboReload(selector) {
    try {
      document.querySelectorAll(selector).forEach((elem) => {
        if (elem.src) elem.reload();
      });
    } catch (ex) {
      console.log(ex);
    }
  }

  static async turboRequest(elem) {
    let response;
    let method = (elem.dataset.method || "GET").toUpperCase();
    let url = elem.dataset.turboUrl;
    let accept = "text/vnd.turbo-stream.html, text/html, application/xhtml+xml";
    if (url) {
      if (method == "GET") {
        response = await fetch(url, {
          method,
          headers: {
            Accept: accept,
            "X-CSRF-Token": this.authenticityToken,
          },
        });
      } else {
        const formData = new FormData();
        formData.append("_method", method.toLowerCase());
        formData.append("authenticity_token", this.authenticityToken);
        response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: accept,
            "X-CSRF-Token": this.authenticityToken,
          },
          body: formData,
        });
      }
      if (response.redirected) {
        let parser = new DOMParser();
        let doc = parser.parseFromString(await response.text(), "text/html");
        let nodes = [...document.head.childNodes];
        doc.head.childNodes.forEach((node) => {
          let index = nodes.findIndex((n) => n.isEqualNode(node));
          if (index < 0) document.head.appendChild(node);
        });

        let html = doc.body.innerHTML;
        if (html.startsWith("<turbo-stream")) {
          Turbo.renderStreamMessage(html);
        } else {
          document.body.innerHTML = html;
          window.history.pushState(null, null, response.url);
          NApp.dispatchTurboLoad();
        }
      } else {
        Turbo.renderStreamMessage(await response.text());
      }

      if (elem.dataset.turboOnce) {
        elem.setAttribute("onclick", "return false;");
      }
    }
  }

  static keyEnterAccepted(event) {
    let accepted = true;
    if (event.key == "Enter") accepted = event.target.tagName == "TEXTAREA";
    return accepted;
  }

  static countTextMessage(target) {
    document.getElementById("char_count").innerHTML = target.value.length;
  }

  static disableAll(selector, value) {
    const items = document.querySelectorAll(selector);
    const disabled = value == true || value == "true";
    if (items.length > 0) {
      items.forEach((item) => {
        item.setAttribute("disabled", disabled);
      });
    }
  }

  static selectAll(selector, value, bubbles = true) {
    const items = document.querySelectorAll(selector);
    if (items.length > 0) {
      items.forEach((item) => {
        item.checked = value;
        NApp.markRowByCheckbox(item);
      });
      items[0].dispatchEvent(new CustomEvent("change", { bubbles }));
    }
  }

  static selectFirst(selector, bubbles = true) {
    const item = document.querySelector(selector);
    if (item) {
      item.checked = true;
      NApp.markRowByCheckbox(item);
      item.dispatchEvent(new CustomEvent("change", { bubbles }));
    }
  }

  static selectLast(selector, bubbles = true) {
    const item = Array.from(document.querySelectorAll(selector)).pop();
    if (item) {
      item.checked = true;
      NApp.markRowByCheckbox(item);
      item.dispatchEvent(new CustomEvent("change", { bubbles }));
    }
  }

  static markRowByCheckbox(checkbox) {
    const row = checkbox.closest("tr,.tabulator-row");
    if (row) {
      if (checkbox.checked) {
        row.classList.add("marked");
      } else {
        row.classList.remove("marked");
      }
    }
  }

  static newPopup(url, name, h, w, l, t) {
    window.open(
      url,
      name,
      `height=${h},width=${w},left=${l},top=${t},resizable=0,scrollbars=1,toolbar=0,menubar=0,location=0,directories=0,status=1`,
    );
  }

  static resizeModal(elemId, opts = {}) {
    const elem = document.getElementById(elemId);
    const modal = elem?.closest(".v--modal");
    if (!modal || !opts.width) return;

    let nextWidth = opts.width;
    let widthType = "px";
    let widthValue = 0;

    if (typeof nextWidth === "number") {
      widthValue = nextWidth;
    } else if (typeof nextWidth === "string") {
      nextWidth = nextWidth.trim();
      if (nextWidth.endsWith("%")) {
        widthType = "%";
        widthValue = parseFloat(nextWidth.replace("%", ""));
      } else if (nextWidth.endsWith("px")) {
        widthValue = parseFloat(nextWidth.replace("px", ""));
      } else {
        widthValue = parseFloat(nextWidth);
      }
    }

    if (!Number.isFinite(widthValue)) return;

    const modalVm = NApp.resolveModalVm(modal);
    if (!modalVm?.modal) return;

    modalVm.modal.widthType = widthType;
    modalVm.modal.width = widthValue;
    if (typeof modalVm.ensureShiftInWindowBounds === "function") {
      modalVm.ensureShiftInWindowBounds();
    }
    if (typeof modalVm.$forceUpdate === "function") modalVm.$forceUpdate();
  }

  static resolveModalVm(modal) {
    const overlay = modal.closest(".v--modal-overlay");
    const modalName = overlay?.dataset?.modal;
    const queue = [];
    const visited = new Set();

    if (overlay?.__vue__) queue.push(overlay.__vue__);
    if (modal.__vue__) queue.push(modal.__vue__);
    (rootWindow().vues || []).forEach((vm) => queue.push(vm));

    while (queue.length > 0) {
      const vm = queue.shift();
      if (!vm || visited.has(vm)) continue;
      visited.add(vm);

      const isModalVm =
        vm?.$options?.name === "VueJsModal" ||
        (vm?.modal && typeof vm.ensureShiftInWindowBounds === "function");
      const matchedRef = vm?.$refs?.modal === modal;
      const matchedName = !modalName || vm?.name === modalName;

      if ((matchedRef || isModalVm) && matchedName && vm?.modal) return vm;

      if (Array.isArray(vm?.$children)) {
        vm.$children.forEach((child) => queue.push(child));
      }
      if (vm?.$parent) queue.push(vm.$parent);
    }

    return null;
  }

  static dispatchEvent(selector, name, detail = {}) {
    document
      .querySelectorAll(selector)
      .forEach((elem) => elem.dispatchEvent(new CustomEvent(name, { detail })));
  }

  static dispatchTurboLoad() {
    document.dispatchEvent(new CustomEvent("turbo:load"));
    document.querySelectorAll("script.turbo").forEach((script) => {
      script.parentNode.removeChild(script);
    });
  }

  static toCurrency(value, options = { min: 2, max: 2 }) {
    if (!["number", "string"].includes(typeof value)) return value;
    if (!value.toString().match(/^-?\d*\.?\d*$/)) return value;
    var formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: options.min,
      maximumFractionDigits: options.max,
    });
    return formatter.format(value);
  }
}

window.fetch = new Proxy(window.fetch, {
  apply: async function (fetch, that, args) {
    // Forward function call to the original fetch
    try {
      NApp.loading();
      return await fetch.apply(that, args);
    } finally {
      NApp.loaded();
    }
  },
});

document.addEventListener("turbo:load", () => {
  setTimeout(() => {
    NApp.flashNoticeOut();
    NApp.disableLink();
    NApp.autoArea();
    NApp.autoRemove();
    NApp.autoNumber();
    NApp.autoSave();
    NApp.autoSum();
    NApp.cancelLink();
    NApp.clearForm();
    NApp.checkAll();
    NApp.inputMask();
    NApp.moneyInput();
    NApp.passwordInput();
    NApp.showHide();
    NApp.syncValue();
    NApp.turboWrapper();
  }, 100);
});
document.addEventListener("turbo:frame-load", () => NApp.dispatchTurboLoad());
document.addEventListener("turbo:before-visit", () => NApp.loading());
document.addEventListener("turbo:load", () => NApp.loaded());
document.addEventListener("turbo:load", () => NApp.ensureZIndex());
window.addEventListener("popstate", () => {
  let meta = document.head.querySelector('meta[name="popstate"]');
  let state = (meta || { content: "default" }).content;
  if (state == "block") {
    history.pushState(null, null, document.URL);
  } else {
    Turbo.visit(window.location.href, { action: "replace" });
  }
});
